// /admin/analytics/trafico — Panel de tráfico web de Limpack.
// Dos fuentes:
//  1) Analytics PROPIO (first-party, tabla analytics_web vía RPCs): personas AHORA, canal de dónde
//     viene la gente, y por dónde entra/navega. Confiable (los bots no ejecutan JS → quedan afuera).
//  2) Cloudflare (edge fn cf-analytics v2): volumen histórico + errores de infraestructura.
//
// La data propia se empieza a llenar cuando el tracking nuevo esté deployado (landing + tienda) y
// la gente visite. Hasta entonces las secciones "propias" pueden verse vacías; es esperado.

import { useEffect, useState, useMemo, useCallback } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { nombrePagina } from '../../lib/nombresPaginas'

const RANGOS = [
  { v: 1,  l: 'Hoy' },
  { v: 7,  l: '7 días' },
  { v: 14, l: '14 días' },
  { v: 30, l: '30 días' },
]

const nf = (n) => Number(n || 0).toLocaleString('es-UY')

// Etiqueta linda + emoji por canal
const CANAL_META = {
  instagram: { l: 'Instagram', e: '📷' },
  facebook:  { l: 'Facebook',  e: '👍' },
  google:    { l: 'Google',    e: '🔍' },
  whatsapp:  { l: 'WhatsApp',  e: '💬' },
  twitter:   { l: 'Twitter / X', e: '🐦' },
  directo:   { l: 'Directo (tipeó la URL / guardado)', e: '🔗' },
  interno:   { l: 'Navegación interna', e: '↩️' },
  otro:      { l: 'Otro sitio', e: '🌐' },
}
const canalInfo = (c) => CANAL_META[c] || { l: c || '—', e: '🏷' }

// Etiqueta de subdominio
function hostLabel(h) {
  if (!h) return ''
  if (h.startsWith('tienda.')) return 'Tienda'
  if (h.startsWith('panel.'))  return 'Panel'
  if (h.startsWith('www.') || h === 'limpack.uy') return 'Landing'
  return h
}

const HTTP_LABEL = {
  200: 'OK', 204: 'Sin contenido', 301: 'Redirect fijo', 302: 'Redirect', 304: 'Cacheado',
  400: 'Petición mala', 401: 'Sin auth', 403: 'Bloqueado (bot/WAF)', 404: 'No existe', 499: 'Usuario cortó',
  500: 'Error interno', 502: 'Upstream mal', 503: 'Fuera de línea', 504: 'Timeout',
}
const fmtDia = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' })
}

export default function AnalyticsTrafico() {
  const [dias, setDias] = useState(7)
  const [modoCF, setModoCF] = useState('personas') // 'personas' | 'todo' (para la sección Cloudflare)

  const [live, setLive] = useState(null)       // { personas, detalle[] }
  const [canales, setCanales] = useState(null) // [{canal, sesiones}]
  const [paginas, setPaginas] = useState(null) // [{host, path, hits, sesiones}]
  const [serie, setSerie] = useState(null)     // [{fecha, paginas, sesiones}]
  const [cf, setCf] = useState(null)           // cf-analytics result
  const [loading, setLoading] = useState(true)
  const [ts, setTs] = useState(null)

  // ---- Personas ahora (rápido, se refresca solo cada 20s) ----
  const cargarLive = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('web_personas_ahora')
      if (!error && data) setLive(data)
    } catch { /* */ }
  }, [])

  // ---- Todo el resto ----
  const cargarTodo = useCallback(async () => {
    setLoading(true)
    const [c, p, s] = await Promise.all([
      supabase.rpc('web_canales', { p_dias: dias }).then(r => r.data).catch(() => null),
      supabase.rpc('web_top_paginas', { p_dias: dias }).then(r => r.data).catch(() => null),
      supabase.rpc('web_serie', { p_dias: dias }).then(r => r.data).catch(() => null),
    ])
    setCanales(c || []); setPaginas(p || []); setSerie(s || [])
    try {
      const { data: cfr } = await supabase.functions.invoke('cf-analytics', { body: { dias, modo: modoCF } })
      if (cfr && !cfr.error) setCf(cfr)
    } catch { /* */ }
    setTs(new Date())
    setLoading(false)
  }, [dias, modoCF])

  useEffect(() => { cargarLive(); cargarTodo() /* eslint-disable-next-line */ }, [dias, modoCF])
  useEffect(() => { const id = setInterval(cargarLive, 20_000); return () => clearInterval(id) }, [cargarLive])
  useEffect(() => { const id = setInterval(cargarTodo, 120_000); return () => clearInterval(id) }, [cargarTodo])

  const maxDia = useMemo(() => Math.max(1, ...(serie || []).map(d => d.paginas || 0)), [serie])
  const totCanales = useMemo(() => (canales || []).reduce((s, c) => s + (c.sesiones || 0), 0) || 1, [canales])
  const maxPagina = useMemo(() => Math.max(1, ...(paginas || []).map(p => p.hits || 0)), [paginas])
  const totVisitas = useMemo(() => (serie || []).reduce((s, d) => s + (d.paginas || 0), 0), [serie])
  const totSesiones = useMemo(() => (serie || []).reduce((s, d) => s + (d.sesiones || 0), 0), [serie])
  const canalPrincipal = useMemo(() => (canales && canales.length ? canales[0] : null), [canales])
  // Desglose honesto de status de Cloudflare: bloqueos (403 = casi siempre bots) vs errores del sitio (5xx) vs 404
  const cfStat = useMemo(() => {
    let bloq403 = 0, err5xx = 0, n404 = 0
    for (const x of (cf?.topStatus || [])) {
      const c = Number(x.status || 0)
      if (c === 403) bloq403 += x.count
      else if (c >= 500) err5xx += x.count
      else if (c === 404) n404 += x.count
    }
    return { bloq403, err5xx, n404 }
  }, [cf])

  const personas = live?.personas || 0

  return (
    <AdminLayout title="Tráfico web · quién entra y de dónde">
      <Estilos />
      <div className="at-wrap">
        <div className="at-header">
          <div>
            <h1>Tráfico web</h1>
            <p className="at-sub">
              Quién está, de qué canal viene y por dónde navega · último refresh {ts ? ts.toLocaleTimeString('es-UY') : '…'}
            </p>
          </div>
          <button className="at-btn" onClick={() => { cargarLive(); cargarTodo() }} disabled={loading}>
            {loading ? '⏳' : '🔄'} Refrescar
          </button>
        </div>

        {/* PERSONAS AHORA */}
        <div className={'at-live ' + (personas > 0 ? 'on' : '')}>
          <div className="at-live-main">
            <span className="at-live-dot" />
            <span className="at-live-num">{personas}</span>
            <span className="at-live-lbl">{personas === 1 ? 'persona ahora' : 'personas ahora'}</span>
            <span className="at-live-hint">en vivo · se actualiza solo</span>
          </div>
          {personas > 0 && (
            <div className="at-live-list">
              {(live.detalle || []).slice(0, 8).map((d, i) => (
                <div key={i} className="at-live-chip">
                  <span>{canalInfo(d.canal).e}</span>
                  <b>{hostLabel(d.host)}</b>
                  <span className="at-live-path">{nombrePagina(d.path) || d.path}</span>
                </div>
              ))}
            </div>
          )}
          {personas === 0 && (
            <div className="at-live-empty">Nadie navegando en este momento (o el tracking todavía no está deployado).</div>
          )}
        </div>

        {/* RANGO */}
        <div className="at-filtros">
          <div className="at-filtro-grupo">
            <span className="at-filtro-lbl">Rango</span>
            {RANGOS.map(r => (
              <button key={r.v} className={`at-filtro ${dias === r.v ? 'is-active' : ''}`} onClick={() => setDias(r.v)}>{r.l}</button>
            ))}
          </div>
        </div>

        {/* KPIs de PERSONAS reales (first-party). El volumen/errores de Cloudflare va en su sección aparte
            para no confundir capas (una persona real ≠ una request de Cloudflare, que incluye bots). */}
        <div className="at-kpi-scope">👥 Personas reales (tracking propio · los bots quedan afuera)</div>
        <div className="at-kpis">
          <div className="at-kpi">
            <div className="at-kpi-lbl">Visitantes</div>
            <div className="at-kpi-num">{nf(totSesiones)}</div>
            <div className="at-kpi-sub">personas · {dias === 1 ? 'hoy' : `${dias} días`}</div>
          </div>
          <div className="at-kpi">
            <div className="at-kpi-lbl">Páginas vistas</div>
            <div className="at-kpi-num">{nf(totVisitas)}</div>
            <div className="at-kpi-sub">por personas reales</div>
          </div>
          <div className="at-kpi">
            <div className="at-kpi-lbl">Páginas por visita</div>
            <div className="at-kpi-num">{totSesiones > 0 ? (totVisitas / totSesiones).toFixed(1) : '—'}</div>
            <div className="at-kpi-sub">cuánto navegan</div>
          </div>
          <div className="at-kpi">
            <div className="at-kpi-lbl">Canal principal</div>
            <div className="at-kpi-num" style={{ fontSize: 19 }}>
              {canalPrincipal ? `${canalInfo(canalPrincipal.canal).e} ${canalInfo(canalPrincipal.canal).l.split(' ')[0]}` : '—'}
            </div>
            <div className="at-kpi-sub">{canalPrincipal && totCanales > 0 ? `${Math.round(canalPrincipal.sesiones / totCanales * 100)}% de las visitas` : 'de dónde vienen'}</div>
          </div>
        </div>

        <div className="at-cols">
          {/* CANALES */}
          <div className="at-card">
            <h3>📣 De qué canal viene la gente</h3>
            {!canales ? <div className="at-empty">Cargando…</div>
              : canales.length === 0 ? <div className="at-empty">Sin datos todavía. Se llena cuando la gente visite con el tracking deployado.</div>
              : (
                <div className="at-bars">
                  {canales.map((c, i) => {
                    const info = canalInfo(c.canal)
                    const pct = Math.round((c.sesiones / totCanales) * 100)
                    return (
                      <div key={i} className="at-bar-row">
                        <div className="at-bar-lbl"><span>{info.e}</span> {info.l}</div>
                        <div className="at-bar-track"><div className="at-bar-fill" style={{ width: pct + '%' }} /></div>
                        <div className="at-bar-val">{nf(c.sesiones)} <span className="at-bar-pct">{pct}%</span></div>
                      </div>
                    )
                  })}
                </div>
              )}
          </div>

          {/* POR DÓNDE ENTRA Y NAVEGA */}
          <div className="at-card">
            <h3>📍 Por dónde entra y navega la gente</h3>
            {!paginas ? <div className="at-empty">Cargando…</div>
              : paginas.length === 0 ? <div className="at-empty">Sin datos todavía.</div>
              : (
                <div className="at-bars">
                  {paginas.slice(0, 12).map((p, i) => {
                    const nombre = nombrePagina(p.path) || p.path
                    const pct = Math.round((p.hits / maxPagina) * 100)
                    return (
                      <div key={i} className="at-bar-row">
                        <div className="at-bar-lbl">
                          <span className="at-host">{hostLabel(p.host)}</span> {nombre}
                          {nombre !== p.path && <span className="at-path">{p.path}</span>}
                        </div>
                        <div className="at-bar-track"><div className="at-bar-fill teal" style={{ width: pct + '%' }} /></div>
                        <div className="at-bar-val">{nf(p.hits)} <span className="at-bar-pct">{nf(p.sesiones)} vis.</span></div>
                      </div>
                    )
                  })}
                </div>
              )}
          </div>
        </div>

        {/* SERIE DIARIA (personas) */}
        <div className="at-card">
          <h3>📈 Visitas por día (personas)</h3>
          {!serie ? <div className="at-empty">Cargando…</div>
            : serie.length === 0 ? <div className="at-empty">Sin datos todavía.</div>
            : (
              <div className="at-serie">
                {serie.map(d => (
                  <div key={d.fecha} className="at-serie-row">
                    <div className="at-serie-fecha">{fmtDia(d.fecha)}</div>
                    <div className="at-serie-bar-wrap">
                      <div className="at-serie-bar" style={{ width: `${(d.paginas / maxDia) * 100}%` }} />
                      <span className="at-serie-num">{nf(d.paginas)} <span style={{ opacity: .6, fontWeight: 500 }}>· {nf(d.sesiones)} vis.</span></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* CLOUDFLARE — volumen + infraestructura */}
        <div className="at-cf-head">
          <h2>🛡 Cloudflare · volumen e infraestructura</h2>
          <div className="at-filtro-grupo">
            <button className={`at-filtro ${modoCF === 'personas' ? 'is-active' : ''}`} onClick={() => setModoCF('personas')}>Solo personas (UY)</button>
            <button className={`at-filtro ${modoCF === 'todo' ? 'is-active' : ''}`} onClick={() => setModoCF('todo')}>Todo (incluye bots)</button>
          </div>
        </div>
        <div className="at-cf-strip">
          <div className="at-cf-stat"><span className="n">{cf ? nf(cf.kpis?.requestsTotales) : '—'}</span><span className="l">Requests (incluye bots)</span></div>
          <div className="at-cf-stat"><span className="n" style={{ color: '#64748B' }}>{cf ? nf(cfStat.bloq403) : '—'}</span><span className="l">Bloqueos CF · 403 (casi siempre bots)</span></div>
          <div className="at-cf-stat"><span className="n" style={{ color: cfStat.n404 > 0 ? '#B87500' : '#1D9E75' }}>{cf ? nf(cfStat.n404) : '—'}</span><span className="l">No existe · 404 (links rotos)</span></div>
          <div className="at-cf-stat"><span className="n" style={{ color: cfStat.err5xx > 0 ? '#C53030' : '#1D9E75' }}>{cf ? nf(cfStat.err5xx) : '—'}</span><span className="l">Errores del sitio · 5xx</span></div>
        </div>
        <p className="at-cf-note">
          Cloudflare no da un “score de bot” en el plan actual. En <b>Solo personas</b> filtramos cargas de página reales (HTML) desde Uruguay, que saca assets y la mayoría de los crawlers. <b>Un 403 es una request que Cloudflare frenó antes de entrar</b> (casi siempre un bot) — no es un cliente tuyo con error, y por eso no aparece en “personas reales” de arriba. Lo único que indica que algo tuyo se rompió es <b>5xx</b>. El detalle de canal y el “ahora” salen del tracking propio.
        </p>

        <div className="at-cols">
          <div className="at-card">
            <h3>🔗 Páginas (Cloudflare, {modoCF === 'personas' ? 'personas' : 'todo'})</h3>
            {!cf ? <div className="at-empty">Cargando…</div>
              : (cf.topPaginas || []).length === 0 ? <div className="at-empty">Sin datos</div>
              : (
                <table className="at-tabla">
                  <thead><tr><th>Página</th><th style={{ textAlign: 'right' }}>Hits</th></tr></thead>
                  <tbody>
                    {cf.topPaginas.slice(0, 12).map((p, i) => {
                      const nombre = nombrePagina(p.path) || p.path
                      return (
                        <tr key={i}>
                          <td><span className="at-host">{hostLabel(p.host)}</span> {nombre}
                            {nombre !== p.path && <div className="at-path2">{p.path}</div>}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{nf(p.count)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
          </div>

          <div className="at-card">
            <h3>⚡ Status codes</h3>
            {!cf ? <div className="at-empty">Cargando…</div> : (
              <table className="at-tabla">
                <thead><tr><th>Code</th><th>Qué es</th><th style={{ textAlign: 'right' }}>Hits</th></tr></thead>
                <tbody>
                  {(cf.topStatus || []).slice(0, 8).map((s, i) => {
                    const c = Number(s.status || 0)
                    const color = c >= 500 ? '#C53030' : c >= 400 ? '#B87500' : c >= 300 ? '#666' : '#1D9E75'
                    return (
                      <tr key={i}>
                        <td style={{ fontWeight: 700, color }}>HTTP {s.status}</td>
                        <td style={{ color: '#64748B', fontSize: 12 }}>{HTTP_LABEL[c] || '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{nf(s.count)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {modoCF === 'todo' && cf?.topPaises && (
            <div className="at-card">
              <h3>🌎 Países</h3>
              <table className="at-tabla">
                <thead><tr><th>País</th><th style={{ textAlign: 'right' }}>Hits</th></tr></thead>
                <tbody>
                  {cf.topPaises.slice(0, 12).map((p, i) => (
                    <tr key={i}><td>{p.pais}</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{nf(p.count)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="at-card">
            <h3>🖥 Navegadores</h3>
            {!cf ? <div className="at-empty">Cargando…</div> : (
              <table className="at-tabla">
                <thead><tr><th>Navegador</th><th style={{ textAlign: 'right' }}>Hits</th></tr></thead>
                <tbody>
                  {(cf.topNavegadores || []).slice(0, 10).map((b, i) => (
                    <tr key={i}><td>{b.browser}</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{nf(b.count)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

function Estilos() {
  return <style>{`
    .at-wrap { padding: 20px 24px; max-width: 1200px; margin: 0 auto }
    .at-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px }
    .at-header h1 { margin: 0; color: #1A3461; font-size: 22px; font-weight: 800 }
    .at-sub { margin: 4px 0 0; color: #64748B; font-size: 12.5px }
    .at-btn { background: #1A3461; color: #fff; border: 0; border-radius: 8px; padding: 10px 16px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; white-space: nowrap }
    .at-btn:disabled { opacity: .6; cursor: wait }

    /* PERSONAS AHORA */
    .at-live { background: #fff; border: 1px solid #E2E8F0; border-left: 5px solid #94A3B8; border-radius: 12px; padding: 14px 18px; margin-bottom: 18px }
    .at-live.on { border-left-color: #10B981; background: linear-gradient(90deg, rgba(16,185,129,.06), #fff 40%) }
    .at-live-main { display: flex; align-items: center; gap: 10px }
    .at-live-dot { width: 12px; height: 12px; border-radius: 50%; background: #CBD5E1 }
    .at-live.on .at-live-dot { background: #10B981; box-shadow: 0 0 0 0 rgba(16,185,129,.6); animation: atpulse 1.8s infinite }
    @keyframes atpulse { 0% { box-shadow: 0 0 0 0 rgba(16,185,129,.5) } 70% { box-shadow: 0 0 0 10px rgba(16,185,129,0) } 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0) } }
    .at-live-num { font-size: 30px; font-weight: 900; color: #1A3461; line-height: 1 }
    .at-live-lbl { font-size: 15px; font-weight: 700; color: #334155 }
    .at-live-hint { margin-left: auto; font-size: 11px; color: #94A3B8 }
    .at-live-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px }
    .at-live-chip { display: flex; align-items: center; gap: 6px; background: #F1F5F9; border: 1px solid #E2E8F0; border-radius: 999px; padding: 5px 11px; font-size: 12px; color: #334155 }
    .at-live-chip b { color: #00A8B5 }
    .at-live-path { color: #64748B }
    .at-live-empty { margin-top: 8px; font-size: 12.5px; color: #94A3B8; font-style: italic }

    .at-filtros { display: flex; gap: 24px; flex-wrap: wrap; margin: 4px 0 18px; padding: 12px 16px; background: #F8F6F1; border-radius: 10px }
    .at-filtro-grupo { display: flex; align-items: center; gap: 6px; flex-wrap: wrap }
    .at-filtro-lbl { font-size: 11px; font-weight: 800; color: #64748B; text-transform: uppercase; letter-spacing: .06em; margin-right: 6px }
    .at-filtro { padding: 6px 12px; background: #fff; border: 1.5px solid #E2E8F0; border-radius: 999px; font-size: 12.5px; font-weight: 700; color: #1A3461; cursor: pointer; font-family: inherit }
    .at-filtro:hover { background: #EDE9DF }
    .at-filtro.is-active { background: #1A3461; color: #fff; border-color: #1A3461 }

    .at-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 18px }
    @media (max-width: 700px) { .at-kpis { grid-template-columns: repeat(2, 1fr) } }
    .at-kpi { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 14px 16px }
    .at-kpi-lbl { font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: .06em }
    .at-kpi-num { font-size: 26px; font-weight: 800; color: #1A3461; margin: 4px 0 2px }
    .at-kpi-sub { font-size: 11px; color: #94A3B8 }
    .at-kpi-scope { font-size: 12px; font-weight: 700; color: #10B981; margin: 2px 0 8px }

    .at-cf-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 4px 0 12px }
    @media (max-width: 700px) { .at-cf-strip { grid-template-columns: repeat(2, 1fr) } }
    .at-cf-stat { background: #fff; border: 1px solid #E2E8F0; border-radius: 10px; padding: 10px 12px; display: flex; flex-direction: column; gap: 2px }
    .at-cf-stat .n { font-size: 20px; font-weight: 800; color: #1A3461 }
    .at-cf-stat .l { font-size: 10.5px; color: #64748B; line-height: 1.3 }

    .at-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 16px 18px; margin-bottom: 16px }
    .at-card h3 { margin: 0 0 12px; font-size: 14px; font-weight: 800; color: #1A3461 }
    .at-cols { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px }
    @media (max-width: 800px) { .at-cols { grid-template-columns: 1fr } }

    /* barras horizontales (canales + páginas) */
    .at-bars { display: flex; flex-direction: column; gap: 10px }
    .at-bar-row { display: grid; grid-template-columns: 1fr 90px auto; align-items: center; gap: 10px }
    @media (max-width: 560px) { .at-bar-row { grid-template-columns: 1fr 60px auto } }
    .at-bar-lbl { font-size: 12.5px; font-weight: 600; color: #334155; display: flex; align-items: center; gap: 6px; min-width: 0 }
    .at-bar-lbl .at-host { font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #fff; background: #1A3461; padding: 2px 6px; border-radius: 5px }
    .at-bar-lbl .at-path { font-size: 10.5px; color: #94A3B8; font-family: ui-monospace, monospace; margin-left: 4px }
    .at-bar-track { height: 8px; background: #F1F5F9; border-radius: 999px; overflow: hidden }
    .at-bar-fill { height: 100%; background: #5BBDD8; border-radius: 999px }
    .at-bar-fill.teal { background: linear-gradient(90deg, #1A3461, #00A8B5) }
    .at-bar-val { font-size: 12.5px; font-weight: 800; color: #1A3461; text-align: right; white-space: nowrap }
    .at-bar-pct { font-size: 10.5px; color: #94A3B8; font-weight: 600 }

    .at-serie { display: flex; flex-direction: column; gap: 6px }
    .at-serie-row { display: flex; align-items: center; gap: 10px }
    .at-serie-fecha { flex: 0 0 120px; font-size: 12px; color: #64748B; text-transform: capitalize }
    .at-serie-bar-wrap { flex: 1; position: relative; height: 22px; background: #F8F6F1; border-radius: 4px; overflow: hidden }
    .at-serie-bar { position: absolute; left: 0; top: 0; bottom: 0; background: linear-gradient(90deg, #1A3461, #00A8B5); border-radius: 4px }
    .at-serie-num { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); font-size: 11.5px; font-weight: 700; color: #1A3461; z-index: 2 }

    .at-empty { padding: 18px; text-align: center; color: #94A3B8; font-style: italic; font-size: 12.5px }

    .at-cf-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin: 26px 0 4px }
    .at-cf-head h2 { margin: 0; font-size: 16px; font-weight: 800; color: #1A3461 }
    .at-cf-note { font-size: 11.5px; color: #94A3B8; margin: 0 0 14px; line-height: 1.5; max-width: 900px }

    .at-tabla { width: 100%; border-collapse: collapse; font-size: 12.5px }
    .at-tabla th { text-align: left; padding: 6px 8px; font-size: 10.5px; font-weight: 800; color: #64748B; text-transform: uppercase; border-bottom: 1px solid #E2E8F0 }
    .at-tabla td { padding: 7px 8px; border-bottom: 1px dashed #EDE9DF; color: #334155; vertical-align: top }
    .at-tabla tr:last-child td { border-bottom: 0 }
    .at-tabla .at-host { font-size: 9px; font-weight: 800; text-transform: uppercase; color: #fff; background: #1A3461; padding: 1px 5px; border-radius: 4px; margin-right: 4px }
    .at-path2 { font-size: 10.5px; color: #94A3B8; font-family: ui-monospace, monospace; margin-top: 2px }
  `}</style>
}
